import { useEffect, useState } from "react";
import StaffShell from "../components/StaffShell";
import { api } from "../lib/api";
import { SERVICES, SERVICE_SELECT_OPTIONS } from "../content/defaults";

export function BlogAdmin() {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [flash, setFlash] = useState("");

  const load = () => api("/staff/blog").then((d) => setPosts(d.posts)).catch(() => {});
  useEffect(load, []);

  const blank = { slug: "", title: "", make: "", model: "", year: "", service: "battery-charging", intro: "", sections: [{ heading: "", text: "" }], source_url: "", status: "draft" };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...editing, year: editing.year ? parseInt(editing.year, 10) : null };
    if (editing._existing) {
      await api(`/staff/blog/${editing._originalSlug}`, { method: "PATCH", body });
    } else {
      await api("/staff/blog", { method: "POST", body });
    }
    setFlash(`Saved "${editing.title}" (${body.status}).`);
    setEditing(null);
    load();
  };

  return (
    <StaffShell title="Blog (make / model / year guides)">
      {flash && <div className="ok-box" data-testid="blog-flash">{flash}</div>}
      <div className="toolbar">
        <button className="btn btn-blue btn-sm" onClick={() => setEditing({ ...blank })} data-testid="blog-new-button">New article</button>
      </div>

      {editing && (
        <div className="card" style={{ padding: 26, marginBottom: 24 }} data-testid="blog-editor">
          <h3 style={{ fontSize: 17, marginBottom: 14 }}>{editing._existing ? `Edit: ${editing._originalSlug}` : "New article"}</h3>
          <form onSubmit={save}>
            <div className="field"><label>Title</label><input required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="blog-title-input" /></div>
            <div className="field-pair">
              <div className="field"><label>Slug</label><input required value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="2023-toyota-corolla-battery-care" data-testid="blog-slug-input" /></div>
              <div className="field"><label>Service</label>
                <select value={editing.service} onChange={(e) => setEditing({ ...editing, service: e.target.value })}>
                  {SERVICES.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
              </div>
            </div>
            <div className="field-pair">
              <div className="field"><label>Make</label><input value={editing.make} onChange={(e) => setEditing({ ...editing, make: e.target.value })} /></div>
              <div className="field"><label>Model</label><input value={editing.model} onChange={(e) => setEditing({ ...editing, model: e.target.value })} /></div>
            </div>
            <div className="field-pair">
              <div className="field"><label>Year</label><input type="number" value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })} /></div>
              <div className="field"><label>Status</label>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="blog-status-select">
                  <option value="draft">Draft</option><option value="published">Published</option>
                </select>
              </div>
            </div>
            <div className="field"><label>Intro</label><textarea required rows={2} value={editing.intro} onChange={(e) => setEditing({ ...editing, intro: e.target.value })} /></div>
            {editing.sections.map((s, i) => (
              <div className="field-pair" key={i}>
                <div className="field"><label>Section heading</label><input value={s.heading} onChange={(e) => setEditing({ ...editing, sections: editing.sections.map((x, j) => j === i ? { ...x, heading: e.target.value } : x) })} /></div>
                <div className="field"><label>Text</label><textarea rows={2} value={s.text} onChange={(e) => setEditing({ ...editing, sections: editing.sections.map((x, j) => j === i ? { ...x, text: e.target.value } : x) })} /></div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing({ ...editing, sections: [...editing.sections, { heading: "", text: "" }] })}>+ section</button>
            </div>
            <div className="field" style={{ marginTop: 12 }}><label>Manufacturer source link</label><input value={editing.source_url} onChange={(e) => setEditing({ ...editing, source_url: e.target.value })} placeholder="https://..." /></div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-blue btn-sm" data-testid="blog-save-button">Save</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {posts === null && <div className="skeleton" style={{ height: 150 }} />}
      {posts && (
        <div className="card" style={{ overflowX: "auto" }}>
          <table className="data" data-testid="blog-table">
            <thead><tr><th>Slug</th><th>Title</th><th>Vehicle</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.slug}>
                  <td className="mono" style={{ fontSize: 12 }}>{p.slug}</td>
                  <td>{p.title}</td>
                  <td className="muted">{[p.year, p.make, p.model].filter(Boolean).join(" ") || "—"}</td>
                  <td><span className={`pill ${p.status}`}>{p.status}</span></td>
                  <td><button className="btn btn-ghost btn-sm" onClick={() => setEditing({ ...p, _existing: true, _originalSlug: p.slug })} data-testid={`blog-edit-${p.slug}`}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </StaffShell>
  );
}

export function AnalyticsAdmin() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api("/staff/analytics").then(setData).catch(() => {});
  }, []);

  if (!data) return <StaffShell title="Website traffic"><div className="skeleton" style={{ height: 240 }} /></StaffShell>;

  const maxDaily = Math.max(1, ...data.daily.map((d) => d.count));

  return (
    <StaffShell title="Website traffic">
      <div className="grid-2" style={{ marginBottom: 18 }}>
        <div className="card" style={{ padding: 22 }} data-testid="analytics-totals">
          <h3 style={{ fontSize: 16, marginBottom: 12 }}>Visits (page views)</h3>
          <div style={{ display: "flex", gap: 26, flexWrap: "wrap", alignItems: "baseline" }}>
            <div><div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 34 }}>{data.total}</div><span className="muted" style={{ fontSize: 12 }}>all time</span></div>
            <div><div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 26 }}>{data.last_30}</div><span className="muted" style={{ fontSize: 12 }}>last 30 days</span></div>
            <div><div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 26 }}>{data.last_7}</div><span className="muted" style={{ fontSize: 12 }}>last 7 days</span></div>
          </div>
          <p className="form-note" style={{ marginTop: 10 }}>
            {data.direct} direct visits · {data.referred} arrived from another site (Google, Yelp, Facebook…)
          </p>
        </div>
        <div className="card" style={{ padding: 22 }} data-testid="analytics-daily">
          <h3 style={{ fontSize: 16, marginBottom: 12 }}>Last 14 days</h3>
          {data.daily.length === 0 && <p className="muted" style={{ fontSize: 13.5 }}>No visits recorded yet.</p>}
          <div style={{ display: "flex", gap: 6, alignItems: "flex-end", height: 90 }}>
            {data.daily.map((d) => (
              <div key={d.date} title={`${d.date}: ${d.count}`} style={{ flex: 1, display: "grid", gap: 4, justifyItems: "center" }}>
                <div style={{ width: "100%", height: `${(d.count / maxDaily) * 70}px`, minHeight: 4, background: "linear-gradient(180deg, var(--blue), var(--red))", borderRadius: 4 }} />
                <span className="mono muted" style={{ fontSize: 9 }}>{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="grid-2" style={{ marginBottom: 18 }}>
        <div className="card" style={{ padding: 22 }} data-testid="analytics-pages">
          <h3 style={{ fontSize: 16, marginBottom: 12 }}>Top pages</h3>
          {data.top_pages.length === 0 && <p className="muted" style={{ fontSize: 13.5 }}>No data yet.</p>}
          <table className="data"><tbody>
            {data.top_pages.map((p) => <tr key={p.value}><td className="mono" style={{ fontSize: 12.5 }}>{p.value}</td><td style={{ textAlign: "right" }}>{p.count}</td></tr>)}
          </tbody></table>
        </div>
        <div className="card" style={{ padding: 22 }} data-testid="analytics-referrers">
          <h3 style={{ fontSize: 16, marginBottom: 12 }}>Where visitors came from</h3>
          {data.top_referrers.length === 0 && <p className="muted" style={{ fontSize: 13.5 }}>No referred traffic yet — direct visits only.</p>}
          <table className="data"><tbody>
            {data.top_referrers.map((r) => <tr key={r.value}><td>{r.value}</td><td style={{ textAlign: "right" }}>{r.count}</td></tr>)}
          </tbody></table>
          {data.utm_sources.length > 0 && (
            <p className="form-note" style={{ marginTop: 10 }}>Campaigns: {data.utm_sources.map((u) => `${u.value} (${u.count})`).join(", ")}</p>
          )}
        </div>
      </div>
      <div className="card" style={{ padding: 22 }} data-testid="analytics-recent">
        <h3 style={{ fontSize: 16, marginBottom: 12 }}>Recent visits</h3>
        <table className="data">
          <thead><tr><th>When</th><th>Page</th><th>From</th><th>Device</th></tr></thead>
          <tbody>
            {data.recent.slice(0, 20).map((v) => (
              <tr key={v.id || v._id}>
                <td className="mono" style={{ fontSize: 11.5 }}>{(v.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="mono" style={{ fontSize: 12.5 }}>{v.path}</td>
                <td>{v.ref_domain || "direct"}</td>
                <td className="muted">{v.device}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </StaffShell>
  );
}

const CONTENT_KEYS = ["home", "seo", "about", "contact", "dealers", "service_battery-charging", "service_wiring-lighting", "service_starting-alternator"];

export function ContentAdmin() {
  const [key, setKey] = useState("home");
  const [draft, setDraft] = useState(null);
  const [published, setPublished] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [flash, setFlash] = useState("");

  const load = (k) => {
    api(`/staff/content/${k}`).then((d) => {
      setDraft(d.draft); setPublished(d.published); setRevisions(d.revisions || []);
    }).catch(() => {});
  };
  useEffect(() => { load(key); /* eslint-disable-next-line */ }, [key]);

  const setField = (k, v) => setDraft({ ...draft, [k]: v });

  const flatten = (obj, prefix = "") => Object.entries(obj || {}).flatMap(([k, v]) =>
    typeof v === "object" && v !== null ? flatten(v, `${prefix}${k}.`) : [[`${prefix}${k}`, v]]);

  const save = async () => { await api(`/staff/content/${key}`, { method: "PUT", body: { draft } }); setFlash("Draft saved. Publish to make it live."); };
  const publish = async () => { await api(`/staff/content/${key}/publish`, { method: "POST" }); setFlash("Published."); load(key); };
  const restore = async (i) => { await api(`/staff/content/${key}/restore`, { method: "POST", body: { index: i } }); setFlash("Revision restored."); load(key); };

  return (
    <StaffShell title="Content editor">
      <div className="toolbar" data-testid="content-key-picker">
        {CONTENT_KEYS.map((k) => (
          <button key={k} className={`btn btn-sm ${k === key ? "btn-blue" : "btn-ghost"}`} onClick={() => setKey(k)}>{k}</button>
        ))}
      </div>
      {flash && <div className="ok-box" data-testid="content-flash">{flash}</div>}
      {draft === null ? <div className="skeleton" style={{ height: 200 }} /> : (
        <div className="card" style={{ padding: 26 }}>
          {flatten(draft).map(([path, val]) => (
            <div className="field" key={path}>
              <label className="mono" style={{ fontSize: 11.5, color: "var(--muted)" }}>{path}</label>
              {String(val).length > 90 || path.includes("paragraphs") || path.includes("text") ? (
                <textarea rows={2} value={val} onChange={(e) => setField(path, e.target.value)} data-testid={`content-field-${path}`} />
              ) : (
                <input value={val} onChange={(e) => setField(path, e.target.value)} data-testid={`content-field-${path}`} />
              )}
            </div>
          ))}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn btn-ghost btn-sm" onClick={save} data-testid="content-save-draft">Save draft</button>
            <button className="btn btn-blue btn-sm" onClick={publish} data-testid="content-publish">Publish</button>
            {revisions.map((r, i) => (
              <button key={i} className="btn btn-ghost btn-sm" onClick={() => restore(i)} data-testid={`content-restore-${i}`}>
                Restore {revisions.length - i - 1 ? `rev -${revisions.length - i - 1}` : "previous"}
              </button>
            ))}
          </div>
        </div>
      )}
    </StaffShell>
  );
}

export function NotificationsAdmin() {
  const [data, setData] = useState(null);
  const [flash, setFlash] = useState("");
  const load = () => api("/staff/notifications").then(setData).catch(() => {});
  useEffect(load, []);

  const process = async () => {
    setFlash("Processing queue…");
    const r = await api("/staff/notifications/process", { method: "POST" });
    setFlash(`Processed: ${r.sent} sent, ${r.failed} failed, ${r.skipped} skipped/blocked.`);
    load();
  };

  const saveTemplate = async (t) => {
    await api("/staff/notifications/templates", { method: "PATCH", body: { kind: t.kind, subject: t.subject, body: t.body, enabled: t.enabled } });
    setFlash(`Template "${t.kind}" saved.`);
    load();
  };

  if (!data) return <StaffShell title="Notifications"><div className="skeleton" style={{ height: 200 }} /></StaffShell>;

  return (
    <StaffShell title="Notifications">
      {flash && <div className="ok-box" data-testid="notifications-flash">{flash}</div>}
      <div className="card" style={{ padding: 22, marginBottom: 18 }} data-testid="notifications-status">
        <h3 style={{ fontSize: 17, marginBottom: 10 }}>Provider readiness</h3>
        <p style={{ fontSize: 14, lineHeight: 1.8, margin: 0 }}>
          Email: <span className={`pill ${data.email_ready ? "paid" : ""}`}>{data.email_ready ? "ready (Resend, managed)" : "no key"}</span>{" "}
          SMS: <span className="pill cancelled">not configured</span>
        </p>
        <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn btn-blue btn-sm" onClick={process} data-testid="notifications-process-button">Process queue now</button>
        </div>
        <p className="form-note" style={{ marginTop: 10 }}>
          Template disabled → jobs stay blocked. Enabled templates send when the queue is processed;
          provider acceptance is tracked per job. SMS updates require customer permission and are not enabled.
        </p>
      </div>

      <div className="card" style={{ padding: 22, marginBottom: 18 }} data-testid="notifications-queue">
        <h3 style={{ fontSize: 17, marginBottom: 12 }}>Queue (latest 100)</h3>
        {data.jobs.length === 0 && <p className="muted" style={{ fontSize: 14 }}>Queue is empty.</p>}
        <table className="data">
          <thead><tr><th>Created</th><th>To</th><th>Template</th><th>Status</th><th>Provider</th></tr></thead>
          <tbody>
            {data.jobs.map((j) => (
              <tr key={j.id} data-testid={`notification-job-${j.id}`}>
                <td className="mono" style={{ fontSize: 11.5 }}>{(j.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td>{j.to}</td><td className="muted">{j.template}</td>
                <td><span className={`pill ${j.status}`}>{j.status}</span></td>
                <td className="mono muted" style={{ fontSize: 11 }}>{j.provider_id ? j.provider_id.slice(0, 12) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "grid", gap: 18 }}>
        {data.templates.map((t, i) => (
          <div className="card" style={{ padding: 22 }} key={t.kind} data-testid={`template-${t.kind}`}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
              <strong style={{ fontFamily: "var(--font-display)" }}>{t.label}</strong>
              <span className={`pill ${t.enabled ? "paid" : ""}`}>{t.enabled ? "enabled" : "disabled"}</span>
              <label className="consent" style={{ margin: 0 }}><input type="checkbox" checked={t.enabled}
                onChange={(e) => setData({ ...data, templates: data.templates.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x) })} /> enable</label>
            </div>
            <div className="field"><label>Subject</label><input value={t.subject} onChange={(e) => setData({ ...data, templates: data.templates.map((x, j) => j === i ? { ...x, subject: e.target.value } : x) })} /></div>
            <div className="field"><label>Body ({"{{fields}}"} from the job payload)</label><textarea rows={5} value={t.body} onChange={(e) => setData({ ...data, templates: data.templates.map((x, j) => j === i ? { ...x, body: e.target.value } : x) })} /></div>
            <button className="btn btn-blue btn-sm" onClick={() => saveTemplate(t)} data-testid={`template-save-${t.kind}`}>Save template</button>
          </div>
        ))}
      </div>
    </StaffShell>
  );
}

export function AssistantSettings() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [flash, setFlash] = useState("");
  const [testOut, setTestOut] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api("/staff/assistant").then((d) => { setData(d); setForm({ ...d.settings, custom_api_key: "" }); }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    await api("/staff/assistant", { method: "PUT", body: form });
    setFlash("Assistant settings saved. The website chat uses them immediately.");
  };

  const test = async () => {
    setBusy(true);
    setTestOut(null);
    try {
      const r = await api("/staff/assistant/test", { method: "POST", body: { message: "My battery keeps going dead overnight — can you help?" } });
      setTestOut(r);
    } catch (e2) { setTestOut({ reply: e2.message, sources: [] }); }
    setBusy(false);
  };

  if (!form) return <StaffShell title="Assistant settings"><div className="skeleton" style={{ height: 200 }} /></StaffShell>;

  return (
    <StaffShell title="Assistant settings">
      {flash && <div className="ok-box" data-testid="assistant-flash">{flash}</div>}
      <form onSubmit={save} className="card" style={{ padding: 26, maxWidth: 860 }} data-testid="assistant-settings-form">
        <div className="field"><label>Greeting</label><textarea rows={2} value={form.greeting} onChange={(e) => setForm({ ...form, greeting: e.target.value })} data-testid="assistant-greeting-input" /></div>
        <div className="field"><label>Tone instructions</label><input value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} /></div>
        <div className="field"><label>Reply prefix (applied to every answer)</label><input value={form.reply_prefix} onChange={(e) => setForm({ ...form, reply_prefix: e.target.value })} placeholder="e.g. Thanks for asking!" /></div>
        <div className="field"><label>Custom instructions for the model</label><textarea rows={3} value={form.custom_instructions} onChange={(e) => setForm({ ...form, custom_instructions: e.target.value })} /></div>

        <h3 style={{ fontSize: 16, margin: "18px 0 10px" }}>Knowledge groups</h3>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
          {data.doc_groups.map((g) => (
            <label className="consent" key={g} style={{ margin: 0 }}>
              <input type="checkbox" checked={form.enabled_docs.length === 0 || form.enabled_docs.includes(g)}
                onChange={(e) => {
                  const cur = form.enabled_docs.length === 0 ? [...data.doc_groups] : [...form.enabled_docs];
                  const next = e.target.checked ? [...new Set([...cur, g])] : cur.filter((x) => x !== g);
                  setForm({ ...form, enabled_docs: next.length === data.doc_groups.length ? [] : next });
                }} />
              <span>{g}</span>
            </label>
          ))}
        </div>

        <h3 style={{ fontSize: 16, margin: "18px 0 10px" }}>Model</h3>
        <div className="field-pair">
          <div className="field"><label>Provider</label>
            <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} data-testid="assistant-provider-select">
              <option value="emergent">Emergent LLM key (built-in)</option>
              <option value="custom">Custom OpenAI-compatible (e.g. OpenRouter)</option>
            </select>
          </div>
          {form.provider === "emergent" ? (
            <div className="field"><label>Model</label>
              <input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="gpt-5.4" data-testid="assistant-model-input" />
            </div>
          ) : (
            <>
              <div className="field"><label>Base URL</label>
                <input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="https://openrouter.ai/api/v1" data-testid="assistant-baseurl-input" />
              </div>
              <div className="field"><label>Model (OpenRouter id)</label>
                <input value={form.custom_model} onChange={(e) => setForm({ ...form, custom_model: e.target.value })} placeholder="meta-llama/llama-3.3-70b-instruct:free" data-testid="assistant-custommodel-input" />
              </div>
              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label>API key {form.custom_api_key_set ? "(a key is stored — leave blank to keep it)" : "(paste provider key; stored server-side only)"}</label>
                <input type="password" value={form.custom_api_key} onChange={(e) => setForm({ ...form, custom_api_key: e.target.value })} data-testid="assistant-apikey-input" />
              </div>
            </>
          )}
        </div>
        <button className="btn btn-blue" data-testid="assistant-save-button">Save settings</button>
      </form>

      <div className="card" style={{ padding: 26, maxWidth: 860, marginTop: 18 }} data-testid="assistant-test-panel">
        <h3 style={{ fontSize: 16, marginBottom: 12 }}>Test the assistant</h3>
        <button type="button" className="btn btn-ghost btn-sm" onClick={test} disabled={busy} data-testid="assistant-test-button">
          {busy ? "Asking…" : "Send test question"}
        </button>
        {testOut && (
          <div style={{ marginTop: 14 }}>
            <div className="msg bot">{testOut.reply}</div>
            {testOut.sources?.length > 0 && <p className="form-note">Sources: {testOut.sources.join(" · ")}</p>}
          </div>
        )}
      </div>
    </StaffShell>
  );
}
